<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && vehicle"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="$emit('close')"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative bg-white border border-gray-200 dark:border-gray-700  dark:bg-black rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <!-- Header -->
            <div class="sticky top-0 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div class="flex items-center">
                <div class="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center mr-3">
                  <svg class="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                  Vehicle Details
                </h3>
              </div>
              <button
                @click="$emit('close')"
                class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Content -->
            <div class="px-6 py-6 bg-white dark:bg-black">
              <!-- Vehicle Information -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                  </svg>
                  Vehicle Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Vehicle Make
                    </label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ vehicle.vehicle_make }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Vehicle Model
                    </label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ vehicle.vehicle_model }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Year
                    </label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ vehicle.vehicle_year }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Plate Number
                    </label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium font-mono">
                      {{ vehicle.plate_number }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Registered Tires
                    </label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ vehicle.tires_count || 0 }} {{ vehicle.tires_count === 1 ? 'Tire' : 'Tires' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Date Added
                    </label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ formatDate(vehicle.created_at) }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Vehicle Photos Section -->
              <div v-if="vehicle.vehicle_photos && vehicle.vehicle_photos.length > 0" class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                  </svg>
                  Vehicle Photos ({{ vehicle.vehicle_photos.length }})
                </h3>
                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  <div
                    v-for="(photo, index) in vehicle.vehicle_photos"
                    :key="photo.vehicle_photo_id"
                    @click="openLightbox(index)"
                    class="relative group cursor-pointer overflow-hidden rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
                  >
                    <img
                      :src="getPhotoUrl(photo.photo_path)"
                      :alt="`Vehicle photo ${index + 1}`"
                      class="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-200"
                    />
                    <!-- Label Badge -->
                    <div class="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold uppercase">
                      {{ photo.photo_side || `Photo ${index + 1}` }}
                    </div>
                    <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Registered Tires Section -->
              <div v-if="vehicle.tires && vehicle.tires.length > 0" class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  Registered Tires ({{ vehicle.tires.length }})
                </h3>
                <div class="space-y-3">
                  <div
                    v-for="tire in vehicle.tires"
                    :key="tire.tire_id"
                    class="bg-gray-50 dark:bg-black rounded-lg p-4"
                  >
                    <div class="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span class="text-gray-500 dark:text-gray-400">Shop:</span>
                        <span class="ml-2 text-gray-900 dark:text-white font-medium">{{ tire.shop }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400">Code:</span>
                        <span class="ml-2 text-gray-900 dark:text-white font-medium">{{ tire.code }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400">Invoice:</span>
                        <span class="ml-2 text-gray-900 dark:text-white font-medium">{{ tire.invoice_number }}</span>
                      </div>
                      <div>
                        <span class="text-gray-500 dark:text-gray-400">Quantity:</span>
                        <span class="ml-2 text-gray-900 dark:text-white font-medium">{{ tire.number_of_purchased }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="vehicle.tires_count === 0" class="mb-6">
                <div class="text-center py-8 text-gray-500 dark:text-gray-400">
                  <svg class="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p>No tires registered for this vehicle yet.</p>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="sticky bottom-0 bg-gray-50 dark:bg-black border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                @click="$emit('close')"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 font-medium"
              >
                Close
              </button>
              <button
                @click="$emit('edit', vehicle)"
                class="px-4 py-2 text-white rounded-lg transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                :style="{ backgroundColor: brandColors.primary }"
              >
                Edit Vehicle
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Lightbox Modal -->
    <Transition name="lightbox">
      <div
        v-if="lightboxOpen"
        class="fixed inset-0 z-[60] bg-black bg-opacity-90 flex items-center justify-center"
        @click="closeLightbox"
      >
        <button
          @click="closeLightbox"
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Previous Button -->
        <button
          v-if="vehicle && vehicle.vehicle_photos && vehicle.vehicle_photos.length > 1"
          @click.stop="previousPhoto"
          class="absolute left-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <!-- Image -->
        <div class="max-w-7xl max-h-[90vh] p-4" @click.stop>
          <img
            v-if="vehicle && vehicle.vehicle_photos && vehicle.vehicle_photos[currentPhotoIndex]"
            :src="getPhotoUrl(vehicle.vehicle_photos[currentPhotoIndex].photo_path)"
            :alt="`Vehicle photo ${currentPhotoIndex + 1}`"
            class="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
          <div class="text-center mt-4 text-white">
            Photo {{ currentPhotoIndex + 1 }} of {{ vehicle?.vehicle_photos?.length || 0 }}
          </div>
        </div>

        <!-- Next Button -->
        <button
          v-if="vehicle && vehicle.vehicle_photos && vehicle.vehicle_photos.length > 1"
          @click.stop="nextPhoto"
          class="absolute right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useBrand } from '@/composables/brand/useBrand';
import type { Vehicle } from '@/types/vehicle';

const { brandColors } = useBrand();

const props = defineProps<{
  isOpen: boolean;
  vehicle: Vehicle | null;
}>();

defineEmits<{
  close: [];
  edit: [vehicle: Vehicle];
}>();

// Lightbox state
const lightboxOpen = ref(false);
const currentPhotoIndex = ref(0);

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function getPhotoUrl(path: string): string {
  const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
  // Below is for local
  // const baseUrl = process.env.VUE_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
  return `${baseUrl}/storage/${path}`;
}

function openLightbox(index: number) {
  currentPhotoIndex.value = index;
  lightboxOpen.value = true;
}

function closeLightbox() {
  lightboxOpen.value = false;
}

function nextPhoto() {
  if (props.vehicle?.vehicle_photos) {
    currentPhotoIndex.value = (currentPhotoIndex.value + 1) % props.vehicle.vehicle_photos.length;
  }
}

function previousPhoto() {
  if (props.vehicle?.vehicle_photos) {
    currentPhotoIndex.value = currentPhotoIndex.value === 0
      ? props.vehicle.vehicle_photos.length - 1
      : currentPhotoIndex.value - 1;
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.95);
}

.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.3s ease;
}

.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}
</style>

